import { useEffect, useRef, useState, useCallback, type CSSProperties } from 'react';
import { useParams } from 'react-router-dom';
import type { ProjectRol, DocumentCategorie } from '../hooks/useProjecten.js';
import { useInstallPrompt } from '../../../core/shared/hooks/useInstallPrompt.js';
import { apiFetch } from '../../../core/shared/hooks/useApi.js';
import { SignatureCanvas, getSignatureRef } from '../../buitendienst/components/SignatureCanvas.js';
import { KIcon } from '../../../core/shared/components/KIcon.js';
import { PLATFORM_NAAM, merkVariabelen } from '../../../core/shared/kordaroBrand.js';
import { projectStatus, bestelbonStatus, type Toon } from '../../klantportaal/utils/portaalWeergave.js';

// Publieke magic-link werf-app voor externe rollen (klant / projectleider / plaatser / leverdienst).
// Lezen: planning + documenten + feedback + handtekeningen (rol-gescoped, geen prijzen/marges).
// Schrijven: foto's uploaden (werf-rollen), feedback plaatsen (iedereen), digitaal ondertekenen (werf + klant).

interface FactuurGoedkeurder { naam: string; rol: ProjectRol; opmerking: string | null; goedgekeurdOp: string }
interface FactuurGoedkeuring { lijst: FactuurGoedkeurder[]; volledig: boolean; ikGoedgekeurd: boolean }
interface PortaalDoc {
  id: string;
  titel: string;
  categorie: DocumentCategorie;
  versie: number;
  bestandsgrootte: number;
  mimeType: string;
  createdAt: string;
  downloadUrl: string | null;
  unitCode: string | null;
  geuploadDoorNaam: string | null;
  goedkeuring: FactuurGoedkeuring | null;
}
interface PortaalUnit {
  code: string;
  status: string;
  keukenType: { code: string } | null;
  bestelbonStatus: 'GEEN' | 'OPGEMAAKT' | 'ONTVANGEN' | 'BEVESTIGD';
  leverdatum: string | null;
  plaatsingsdatum: string | null;
  opleveringsdatum: string | null;
  schadeOpen: boolean;
}
interface PortaalComment {
  id: string;
  tekst: string;
  auteurNaam: string | null;
  auteurRol: ProjectRol;
  createdAt: string;
  unitCode: string | null;
  vanMij: boolean;
}
interface PortaalHandtekening {
  id: string;
  ondertekenaarNaam: string;
  ondertekenaarRol: ProjectRol;
  verklaring: string | null;
  ondertekendOp: string;
  documentId: string | null;
  unitCode: string | null;
}
interface PortaalBranding { primairHex: string; secundairHex: string; naam: string; isDefault: boolean; logoUrl: string | null }
interface PortaalData {
  rol: ProjectRol;
  genodigde: { naam: string | null; email: string | null };
  preview?: boolean;
  branding: PortaalBranding;
  rechten: { magUploaden: boolean; magOndertekenen: boolean; magFeedback: boolean; magFacturenGoedkeuren: boolean };
  project: { id: string; entityId: string; naam: string; klantNaam: string | null; adres: string | null; aantalUnits: number; status: string };
  documenten: PortaalDoc[];
  units: PortaalUnit[];
  opmerkingen: PortaalComment[];
  handtekeningen: PortaalHandtekening[];
}

function pDatum(s: string | null): string {
  return s ? new Date(s).toLocaleDateString('nl-BE', { timeZone: 'Europe/Brussels', day: '2-digit', month: '2-digit', year: '2-digit' }) : '—';
}
function pDatumTijd(s: string): string {
  return new Date(s).toLocaleString('nl-BE', { timeZone: 'Europe/Brussels', day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}
const TOON_KLASSE: Record<Toon, string> = {
  neutraal: 'k-status', info: 'k-status k-status--info', success: 'k-status k-status--success',
  warning: 'k-status k-status--warning', danger: 'k-status k-status--danger',
};
const SELECT = 'k-field !w-full';

const CATEGORIE_LABEL: Record<DocumentCategorie, string> = {
  BOUWPLAN: 'Bouwplannen',
  OFFERTE: 'Offertes',
  RENDER: 'Renders',
  CONTRACT: 'Contracten',
  CORRESPONDENTIE: 'Correspondentie',
  JURIDISCH: 'Juridisch',
  FINANCIEEL: 'Financieel',
  FACTUUR: 'Facturen',
  OPLEVERING: 'Oplevering & nazorg',
  WERFFOTO: 'Werffoto\'s',
  ALGEMEEN: 'Algemeen',
};
const ROL_LABEL: Record<ProjectRol, string> = {
  VERANTWOORDELIJKE: 'Verantwoordelijke',
  VERKOPER: 'Verkoper',
  FRANCHISENEMER: 'Franchisenemer',
  KLANT: 'Klant',
  PROJECTLEIDER: 'Projectleider',
  EINDKLANT: 'Eindklant',
  PLAATSER: 'Plaatser',
  LEVERDIENST: 'Leverdienst',
};

export function ProjectPortaalPage({ preview = false }: { preview?: boolean } = {}) {
  const { token, id, lidId } = useParams<{ token?: string; id?: string; lidId?: string }>();
  const [data, setData] = useState<PortaalData | null>(null);
  const [status, setStatus] = useState<'laden' | 'ok' | 'ongeldig' | 'fout'>('laden');
  const { canInstall, isInstalled, install } = useInstallPrompt();

  const laad = useCallback(async () => {
    try {
      if (preview) {
        // Interne read-only "Bekijk als": authed endpoint, geen magic-link.
        if (!id || !lidId) { setStatus('fout'); return; }
        const d = await apiFetch<PortaalData>(`/api/projecten/${id}/leden/${lidId}/portaal-preview`);
        setData(d);
        setStatus('ok');
        return;
      }
      if (!token) return;
      const r = await fetch(`/api/public/projecten-portaal/${token}`);
      if (r.status === 404) { setStatus('ongeldig'); return; }
      if (!r.ok) throw new Error('fout');
      setData(await r.json());
      setStatus('ok');
    } catch {
      setStatus('fout');
    }
  }, [token, preview, id, lidId]);

  useEffect(() => { laad(); }, [laad]);

  // PWA: wijs het manifest + apple-touch-icon naar dit projectportaal, zodat "Zet op beginscherm"
  // een app installeert die rechtstreeks dit project opent (met winkel-branding). Niet in preview.
  useEffect(() => {
    if (preview || status !== 'ok' || !data || !token) return;
    const head = document.head;
    const setLink = (rel: string, href: string, extra?: Record<string, string>) => {
      let el = head.querySelector<HTMLLinkElement>(`link[data-portaal="${rel}"]`);
      if (!el) { el = document.createElement('link'); el.setAttribute('data-portaal', rel); head.appendChild(el); }
      el.rel = rel; el.href = href;
      if (extra) for (const [k, v] of Object.entries(extra)) el.setAttribute(k, v);
    };
    setLink('manifest', `/api/public/projecten-portaal/${token}/manifest.webmanifest`);
    setLink('apple-touch-icon', `/api/public/pwa/icon/${data.project.entityId}/192.png`);
    let title = head.querySelector<HTMLMetaElement>('meta[name="apple-mobile-web-app-title"][data-portaal="1"]');
    if (!title) { title = document.createElement('meta'); title.name = 'apple-mobile-web-app-title'; title.setAttribute('data-portaal', '1'); head.appendChild(title); }
    title.content = data.project.naam.slice(0, 15);
    return () => {
      head.querySelectorAll('[data-portaal]').forEach((el) => el.remove());
    };
  }, [status, data, token]);

  // Foto-raster = afbeeldingen uit de werf-/nazorg-categorieën (werffoto's + opleverings-/schadefoto's).
  // Zo staan foto's nooit meer tussen de PDF's (bv. DNV-rapporten), maar blijven bouwplannen/renders/
  // contracten gewoon bij de documenten — ook als die toevallig een .png/.jpg zijn.
  const FOTO_CATEGORIEEN: DocumentCategorie[] = ['WERFFOTO', 'OPLEVERING'];
  const isAfb = (d: PortaalDoc) => !!d.mimeType?.startsWith('image/');
  const isWerffoto = (d: PortaalDoc) => isAfb(d) && FOTO_CATEGORIEEN.includes(d.categorie);
  const isRender = (d: PortaalDoc) => isAfb(d) && d.categorie === 'RENDER';
  const fotos = (data?.documenten ?? []).filter(isWerffoto);
  const renders = (data?.documenten ?? []).filter(isRender);
  const overigeDocs = (data?.documenten ?? []).filter((d) => !isWerffoto(d) && !isRender(d));
  const perCategorie = overigeDocs.reduce<Record<string, PortaalDoc[]>>((acc, d) => {
    (acc[d.categorie] ??= []).push(d);
    return acc;
  }, {});
  const merkNaam = data?.branding && !data.branding.isDefault ? data.branding.naam : PLATFORM_NAAM;
  const stijl = merkVariabelen(data?.branding?.primairHex) as CSSProperties;
  const st = data ? projectStatus(data.project.status) : null;

  return (
    <div className="k-ui min-h-[100dvh]" style={stijl}>
      {data?.preview && (
        <div className="k-status k-status--warning !flex !rounded-none !px-4 !py-2 sticky top-0 z-20" role="status">
          <KIcon naam="info" size={16} />
          <span className="flex-1">Voorbeeld: zo ziet <strong>{data.genodigde.naam || 'dit contact'}</strong> ({ROL_LABEL[data.rol]}) het portaal. Acties zijn uitgeschakeld.</span>
          <button type="button" onClick={() => window.close()} className="k-link shrink-0 min-h-[44px]">Sluiten</button>
        </div>
      )}
      <header className="k-shell-header">
        <div className="max-w-3xl mx-auto px-4 md:px-6 py-2 min-h-[56px] flex items-center justify-between gap-2">
          <div className="flex items-center gap-2 min-w-0">
            {data?.branding?.logoUrl && (
              <img
                src={data.branding.logoUrl}
                alt=""
                className="h-8 max-w-[120px] object-contain shrink-0"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
              />
            )}
            <span className="font-semibold truncate" style={{ color: 'var(--k-brand)' }}>{merkNaam}</span>
            <span className="k-meta truncate">Projectportaal</span>
          </div>
          {canInstall && !isInstalled && (
            <button type="button" onClick={() => install()} className="k-button k-button--secondary shrink-0">App installeren</button>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 md:px-6 py-6">
        {status === 'laden' && <p className="k-meta" role="status" aria-busy="true">Project wordt geladen…</p>}

        {status === 'ongeldig' && (
          <div className="k-empty mt-8">
            <span className="inline-flex text-[var(--k-muted)] mb-4"><KIcon naam="slot" size={40} /></span>
            <h1 className="k-empty__title">Deze link werkt niet meer</h1>
            <p>De link is verlopen of vervangen. Vraag uw contactpersoon om een nieuwe uitnodiging.</p>
          </div>
        )}
        {status === 'fout' && (
          <div className="k-empty mt-8">
            <span className="inline-flex text-[var(--k-muted)] mb-4"><KIcon naam="offline" size={40} /></span>
            <h1 className="k-empty__title">Het project kan nu niet geladen worden</h1>
            <p>Dit ligt niet aan u. Controleer uw verbinding of probeer het over enkele minuten opnieuw.</p>
            <button type="button" className="k-button" onClick={() => { setStatus('laden'); laad(); }}>Opnieuw proberen</button>
          </div>
        )}

        {status === 'ok' && data && (
          <div className="space-y-8">
            {/* Projectkop */}
            <div>
              {data.genodigde.naam && <p className="k-meta">Welkom, {data.genodigde.naam}</p>}
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <h1 className="k-title">{data.project.naam}</h1>
                {st && <span className={TOON_KLASSE[st.toon]}>{st.tekst}</span>}
              </div>
              <p className="k-meta mt-1">
                {[data.project.klantNaam, data.project.adres, `${data.project.aantalUnits} keukens`].filter(Boolean).join(' · ')}
              </p>
              <p className="k-meta">U bekijkt dit als {ROL_LABEL[data.rol]}.</p>
            </div>

            {/* Install-hint op iOS (geen beforeinstallprompt) */}
            {!isInstalled && !canInstall && (
              <p className="k-meta">Zet dit project als app op uw gsm: tik op <strong>Deel</strong> en dan <strong>Zet op beginscherm</strong> (iPhone), of via het browsermenu op Android.</p>
            )}

            {/* Planning per keuken */}
            {data.units.length > 0 && (
              <section aria-labelledby="k-p-planning">
                <h2 id="k-p-planning" className="k-section-title mb-2">Planning per keuken</h2>
                {/* Gsm: label/waarde per keuken, zodat geen kolom buiten beeld valt */}
                <ul className="md:hidden border-t border-[var(--k-border)]">
                  {data.units.map((u) => (
                    <li key={u.code} className="k-row !py-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold">{u.code}</span>
                        <OpleverStatus u={u} />
                      </div>
                      <dl className="grid grid-cols-3 gap-2 mt-1">
                        <div><dt className="k-meta">Bestelbon</dt><dd>{bestelbonStatus(u.bestelbonStatus) ?? '—'}</dd></div>
                        <div><dt className="k-meta">Levering</dt><dd>{pDatum(u.leverdatum)}</dd></div>
                        <div><dt className="k-meta">Plaatsing</dt><dd>{pDatum(u.plaatsingsdatum)}</dd></div>
                      </dl>
                    </li>
                  ))}
                </ul>
                <div className="k-tabel-scroll hidden md:block" tabIndex={0} role="region" aria-labelledby="k-p-planning">
                  <table className="k-tabel">
                    <thead>
                      <tr>
                        <th scope="col">Keuken</th>
                        <th scope="col">Bestelbon</th>
                        <th scope="col">Levering</th>
                        <th scope="col">Plaatsing</th>
                        <th scope="col">Oplevering</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.units.map((u) => (
                        <tr key={u.code}>
                          <th scope="row" className="!font-semibold">{u.code}</th>
                          <td>{bestelbonStatus(u.bestelbonStatus) ?? <span className="k-leeg">—<span className="k-sr-only"> nog geen bestelbon</span></span>}</td>
                          <td>{pDatum(u.leverdatum)}</td>
                          <td>{pDatum(u.plaatsingsdatum)}</td>
                          <td>
                            <OpleverStatus u={u} />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {/* Werf-acties: foto uploaden (werf-rollen) */}
            {data.rechten.magUploaden && (
              <FotoUpload token={token!} units={data.units.map((u) => u.code)} onDone={laad} />
            )}

            {/* Foto's van de werf — alle afbeeldingen als thumbnails (los van de documenten) */}
            {fotos.length > 0 && (
              <section aria-labelledby="k-p-fotos">
                <h2 id="k-p-fotos" className="k-section-title mb-2">Foto&apos;s van de werf ({fotos.length})</h2>
                <FotoRaster items={fotos} kolommen="grid-cols-3 sm:grid-cols-4" verhouding="aspect-square" />
              </section>
            )}

            {/* Renders — 3D-beelden als thumbnails (apart van de werffoto's) */}
            {renders.length > 0 && (
              <section aria-labelledby="k-p-renders">
                <h2 id="k-p-renders" className="k-section-title mb-2">Renders ({renders.length})</h2>
                <FotoRaster items={renders} kolommen="grid-cols-2 sm:grid-cols-3" verhouding="aspect-[4/3]" />
              </section>
            )}

            {/* Documenten per categorie */}
            {Object.entries(perCategorie).map(([cat, docs]) => (
              <section key={cat} aria-label={CATEGORIE_LABEL[cat as DocumentCategorie]}>
                <h2 className="k-section-title mb-2">{CATEGORIE_LABEL[cat as DocumentCategorie]} ({docs.length})</h2>
                <ul className="border-t border-[var(--k-border)]">
                  {docs.map((d) => (
                    <li key={d.id} className="k-row !min-h-0 !py-3">
                      {d.downloadUrl ? (
                        <a href={d.downloadUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 no-underline text-[var(--k-text)] min-h-[44px]">
                          <span className="text-[var(--k-muted)]"><KIcon naam="documenten" /></span>
                          <span className="flex-1 min-w-0">
                            <span className="block font-medium break-words">{d.titel}</span>
                            <span className="k-meta block">{[d.unitCode, d.versie > 1 ? `versie ${d.versie}` : null, pDatum(d.createdAt)].filter(Boolean).join(' · ')}</span>
                          </span>
                          <span className="k-link inline-flex items-center gap-1 shrink-0"><KIcon naam="download" /> Openen<span className="k-sr-only"> {d.titel}</span></span>
                        </a>
                      ) : (
                        <div className="flex items-center gap-3 min-h-[44px]">
                          <span className="text-[var(--k-muted)]"><KIcon naam="documenten" /></span>
                          <span className="flex-1 min-w-0">
                            <span className="block font-medium break-words">{d.titel}</span>
                            <span className="k-meta block">Dit bestand kan hier niet geopend worden; vraag het aan uw contactpersoon.</span>
                          </span>
                        </div>
                      )}
                      {d.categorie === 'FACTUUR' && d.goedkeuring && (
                        <FactuurAkkoord doc={d} token={token!} magGoedkeuren={data.rechten.magFacturenGoedkeuren} onDone={laad} />
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ))}

            {!data.documenten.length && (
              <div className="k-empty k-panel">
                <p className="k-empty__title">Nog geen documenten</p>
                <p>Er zijn nog geen documenten met u gedeeld. Vraag het na bij uw contactpersoon als u iets mist.</p>
              </div>
            )}

            {/* Ondertekenen (werf + klant) */}
            {data.rechten.magOndertekenen && (
              <Ondertekenen
                token={token!}
                rolNaam={data.genodigde.naam ?? ''}
                units={data.units.map((u) => u.code)}
                docs={data.documenten.filter((d) => d.categorie === 'OPLEVERING' || d.categorie === 'CONTRACT' || d.categorie === 'JURIDISCH')}
                onDone={laad}
              />
            )}

            {/* Reeds gezette handtekeningen */}
            {data.handtekeningen.length > 0 && (
              <section aria-labelledby="k-p-handtekeningen">
                <h2 id="k-p-handtekeningen" className="k-section-title mb-2">Ondertekeningen ({data.handtekeningen.length})</h2>
                <ul className="border-t border-[var(--k-border)]">
                  {data.handtekeningen.map((h) => (
                    <li key={h.id} className="k-row !min-h-0 !py-3 flex items-start gap-3">
                      <span className="text-[var(--k-success)] mt-0.5"><KIcon naam="vink" /></span>
                      <div className="min-w-0 flex-1">
                        <p><span className="font-medium">{h.ondertekenaarNaam}</span> · {ROL_LABEL[h.ondertekenaarRol]}{h.unitCode ? ` · ${h.unitCode}` : ''}</p>
                        {h.verklaring && <p className="k-meta break-words">{h.verklaring}</p>}
                        <p className="k-meta">{pDatumTijd(h.ondertekendOp)}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {/* Feedback */}
            <Feedback
              token={token ?? ''}
              rol={data.rol}
              canPost={data.rechten.magFeedback}
              units={data.units.map((u) => u.code)}
              opmerkingen={data.opmerkingen}
              onDone={laad}
            />
          </div>
        )}
      </main>
    </div>
  );
}

function OpleverStatus({ u }: { u: PortaalUnit }) {
  if (u.schadeOpen) return <span className="k-status k-status--danger"><KIcon naam="let" size={16} /> Schade open</span>;
  if (u.opleveringsdatum) return <span className="k-status k-status--success"><KIcon naam="vink" size={16} /> Opgeleverd {pDatum(u.opleveringsdatum)}</span>;
  return <span className="k-leeg k-meta">—<span className="k-sr-only"> nog niet opgeleverd</span></span>;
}

function FotoRaster({ items, kolommen, verhouding }: { items: PortaalDoc[]; kolommen: string; verhouding: string }) {
  return (
    <ul className={`grid gap-2 ${kolommen}`}>
      {items.map((f) => {
        const naam = `${f.titel}${f.unitCode ? ` · ${f.unitCode}` : ''}`;
        const beeld = (
          <>
            <div className={`${verhouding} rounded-[var(--k-radius-control)] overflow-hidden border border-[var(--k-border)] bg-[var(--k-surface)]`}>
              {f.downloadUrl
                ? <img src={f.downloadUrl} alt={naam} loading="lazy" className="w-full h-full object-cover" />
                : <div className="w-full h-full grid place-items-center text-[var(--k-muted)]"><KIcon naam="fotos" /></div>}
            </div>
            {f.unitCode && <p className="k-meta mt-1 truncate">{f.unitCode}</p>}
          </>
        );
        return (
          <li key={f.id}>
            {f.downloadUrl
              ? <a href={f.downloadUrl} target="_blank" rel="noopener noreferrer" className="block no-underline text-[var(--k-text)]">{beeld}</a>
              : beeld}
          </li>
        );
      })}
    </ul>
  );
}

// ─── Factuur-goedkeuring (werfleider + facturen-ontvanger) ───────────────────
function FactuurAkkoord({ doc, token, magGoedkeuren, onDone }: { doc: PortaalDoc; token: string; magGoedkeuren: boolean; onDone: () => void }) {
  const [busy, setBusy] = useState(false);
  const [fout, setFout] = useState('');
  const gk = doc.goedkeuring!;

  async function keurGoed() {
    setBusy(true); setFout('');
    try {
      const r = await fetch(`/api/public/projecten-portaal/${token}/facturen/${doc.id}/goedkeuren`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({}),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.message || 'Goedkeuren mislukt');
      await onDone();
    } catch (e) {
      setFout(e instanceof Error ? e.message : 'Goedkeuren mislukt');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
      {gk.volledig ? (
        <span className="k-status k-status--success"><KIcon naam="vink" size={16} /> Volledig goedgekeurd</span>
      ) : (
        <span className="k-status k-status--warning"><KIcon naam="klok" size={16} /> Wacht op goedkeuring</span>
      )}
      {gk.lijst.map((g, i) => (
        <span key={i} className="k-meta">{g.naam} ({ROL_LABEL[g.rol]}) keurde goed</span>
      ))}
      {magGoedkeuren && !gk.ikGoedgekeurd && (
        <button type="button" onClick={keurGoed} disabled={busy} aria-busy={busy || undefined} className="k-button">{busy ? 'Bezig…' : 'Goedkeuren'}</button>
      )}
      {magGoedkeuren && gk.ikGoedgekeurd && <span className="k-meta font-medium">U hebt goedgekeurd</span>}
      <span aria-live="polite" className="k-error">{fout}</span>
    </div>
  );
}

// ─── Foto-upload (werf-rollen) ───────────────────────────────────────────────
function FotoUpload({ token, units, onDone }: { token: string; units: string[]; onDone: () => void }) {
  const [unitCode, setUnitCode] = useState('');
  const [titel, setTitel] = useState('');
  const [busy, setBusy] = useState(false);
  const [fout, setFout] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  async function upload(file: File) {
    setBusy(true); setFout('');
    try {
      const fd = new FormData();
      fd.append('foto', file);
      if (unitCode) fd.append('unitCode', unitCode);
      if (titel.trim()) fd.append('titel', titel.trim());
      const r = await fetch(`/api/public/projecten-portaal/${token}/fotos`, { method: 'POST', body: fd });
      if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.message || 'Upload mislukt');
      setTitel('');
      await onDone();
    } catch (e) {
      setFout(e instanceof Error ? e.message : 'Upload mislukt');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  return (
    <section aria-labelledby="k-p-upload" className="k-panel space-y-3">
      <h2 id="k-p-upload" className="k-section-title">Foto van de werf toevoegen</h2>
      {units.length > 0 && (
        <div>
          <label htmlFor="k-p-upload-unit" className="k-label block mb-1">Keuken</label>
          <select id="k-p-upload-unit" value={unitCode} onChange={(e) => setUnitCode(e.target.value)} className={SELECT}>
            <option value="">Hele project</option>
            {units.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      )}
      <div>
        <label htmlFor="k-p-upload-titel" className="k-label block mb-1">Omschrijving (niet verplicht)</label>
        <input id="k-p-upload-titel" value={titel} onChange={(e) => setTitel(e.target.value)} className="k-field" />
      </div>
      {/* Géén capture-attribuut → iOS/Android tonen zowel "Foto maken" als "Kies uit bibliotheek". */}
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="k-sr-only"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) upload(f); }}
      />
      <button type="button" onClick={() => inputRef.current?.click()} disabled={busy} aria-busy={busy || undefined} className="k-button w-full">
        <KIcon naam="fotos" /> {busy ? 'Bezig…' : 'Foto maken of kiezen'}
      </button>
      <p aria-live="polite" className="k-error">{fout ? `${fout}. De foto is niet opgeslagen; probeer opnieuw.` : ''}</p>
    </section>
  );
}

// ─── Feedback ────────────────────────────────────────────────────────────────
function Feedback({ token, rol, canPost, units, opmerkingen, onDone }: { token: string; rol: ProjectRol; canPost: boolean; units: string[]; opmerkingen: PortaalComment[]; onDone: () => void }) {
  const [tekst, setTekst] = useState('');
  const [unitCode, setUnitCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [fout, setFout] = useState('');

  async function plaats() {
    if (tekst.trim().length < 1) return;
    setBusy(true); setFout('');
    try {
      const r = await fetch(`/api/public/projecten-portaal/${token}/opmerkingen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tekst: tekst.trim(), unitCode: unitCode || undefined }),
      });
      if (!r.ok) throw new Error('Plaatsen mislukt');
      setTekst(''); setUnitCode('');
      await onDone();
    } catch (e) {
      setFout(e instanceof Error ? e.message : 'Plaatsen mislukt');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section aria-labelledby="k-p-feedback" className="k-panel space-y-3">
      <h2 id="k-p-feedback" className="k-section-title">Feedback en opmerkingen</h2>
      {canPost && (
        <div className="space-y-3">
          {units.length > 0 && (
            <div>
              <label htmlFor="k-p-feedback-unit" className="k-label block mb-1">Over</label>
              <select id="k-p-feedback-unit" value={unitCode} onChange={(e) => setUnitCode(e.target.value)} className={SELECT}>
                <option value="">Hele project</option>
                {units.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          )}
          <div>
            <label htmlFor="k-p-feedback-tekst" className="k-label block mb-1">Uw opmerking</label>
            <textarea
              id="k-p-feedback-tekst"
              value={tekst}
              onChange={(e) => setTekst(e.target.value)}
              placeholder={rol === 'KLANT' || rol === 'EINDKLANT' ? 'Een vraag of opmerking over uw keuken…' : 'Een opmerking vanaf de werf…'}
              className="k-field"
            />
          </div>
          <button type="button" onClick={plaats} disabled={busy || !tekst.trim()} aria-busy={busy || undefined} className="k-button">{busy ? 'Bezig…' : 'Plaatsen'}</button>
          <p aria-live="polite" className="k-error">{fout ? 'Niet geplaatst. Uw tekst staat er nog; probeer opnieuw.' : ''}</p>
        </div>
      )}
      {!canPost && !opmerkingen.length && <p className="k-meta">Nog geen opmerkingen.</p>}

      {opmerkingen.length > 0 && (
        <ul className="border-t border-[var(--k-border)]">
          {opmerkingen.map((o) => (
            <li key={o.id} className="k-row !min-h-0 !py-3">
              <p className="k-meta">
                <span className="font-medium text-[var(--k-text)]">{o.vanMij ? 'U' : o.auteurNaam ?? 'Onbekend'}</span>
                {' · '}{ROL_LABEL[o.auteurRol]}{o.unitCode ? ` · ${o.unitCode}` : ''} · {pDatumTijd(o.createdAt)}
              </p>
              <p className="whitespace-pre-wrap break-words">{o.tekst}</p>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

// ─── Ondertekenen (werf + klant) ─────────────────────────────────────────────
function Ondertekenen({ token, rolNaam, units, docs, onDone }: { token: string; rolNaam: string; units: string[]; docs: PortaalDoc[]; onDone: () => void }) {
  const [open, setOpen] = useState(false);
  const [unitCode, setUnitCode] = useState('');
  const [documentId, setDocumentId] = useState('');
  const [verklaring, setVerklaring] = useState('Gelezen en goedgekeurd — oplevering');
  const [busy, setBusy] = useState(false);
  const [fout, setFout] = useState('');
  const [hasContent, setHasContent] = useState(false);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);

  const setCanvasEl = useCallback((el: HTMLDivElement | null) => {
    if (el) canvasRef.current = el.querySelector('canvas');
  }, []);

  async function teken() {
    const ref = getSignatureRef(canvasRef.current);
    if (!ref || ref.isEmpty()) { setFout('Zet eerst je handtekening.'); return; }
    if (!ref.isMinimumSize()) { setFout('De handtekening is te klein.'); return; }
    setBusy(true); setFout('');
    try {
      const r = await fetch(`/api/public/projecten-portaal/${token}/ondertekeningen`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          handtekeningData: ref.toDataURL('image/png', 1.0),
          unitCode: unitCode || undefined,
          documentId: documentId || undefined,
          verklaring: verklaring.trim() || undefined,
        }),
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({})))?.message || 'Ondertekenen mislukt');
      setOpen(false); ref.clear();
      await onDone();
    } catch (e) {
      setFout(e instanceof Error ? e.message : 'Ondertekenen mislukt');
    } finally {
      setBusy(false);
    }
  }

  if (!open) {
    return (
      <button type="button" onClick={() => setOpen(true)} className="k-button k-button--secondary w-full">
        <KIcon naam="handtekening" /> Digitaal ondertekenen
      </button>
    );
  }

  return (
    <section aria-labelledby="k-p-teken" className="k-panel space-y-3">
      <h2 id="k-p-teken" className="k-section-title">Digitaal ondertekenen</h2>
      {units.length > 0 && (
        <div>
          <label htmlFor="k-p-teken-unit" className="k-label block mb-1">Keuken</label>
          <select id="k-p-teken-unit" value={unitCode} onChange={(e) => setUnitCode(e.target.value)} className={SELECT}>
            <option value="">Hele project</option>
            {units.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
      )}
      {docs.length > 0 && (
        <div>
          <label htmlFor="k-p-teken-doc" className="k-label block mb-1">Document</label>
          <select id="k-p-teken-doc" value={documentId} onChange={(e) => setDocumentId(e.target.value)} className={SELECT}>
            <option value="">Geen specifiek document</option>
            {docs.map((d) => <option key={d.id} value={d.id}>{d.titel}</option>)}
          </select>
        </div>
      )}
      <div>
        <label htmlFor="k-p-teken-verklaring" className="k-label block mb-1">Verklaring</label>
        <input id="k-p-teken-verklaring" value={verklaring} onChange={(e) => setVerklaring(e.target.value)} className="k-field" />
      </div>
      <div ref={setCanvasEl}>
        <SignatureCanvas height={200} onChange={setHasContent} initialName={rolNaam} showNameDate={false} />
      </div>
      <p aria-live="polite" className="k-error">{fout}</p>
      <div className="flex flex-col-reverse sm:flex-row gap-2">
        <button type="button" onClick={() => { setOpen(false); setFout(''); }} disabled={busy} className="k-button k-button--secondary flex-1">Annuleren</button>
        <button type="button" onClick={teken} disabled={busy || !hasContent} aria-busy={busy || undefined} className="k-button flex-1">{busy ? 'Bezig…' : 'Ondertekenen'}</button>
      </div>
    </section>
  );
}
